import React, { useMemo, useState } from 'react';
import {
  Card,
  Typography,
  Input,
  Switch,
  Button,
  Divider,
  Collapse,
  Segmented,
  App,
  Alert,
  Space,
  Tag,
} from 'antd';
import { useGlobalStore } from '@digiwedge/global-state';
import { normalizeBase, redact, getEnv, displayBase } from '../utils';
import { useIdp } from '../../auth/IdpProvider.js';

function useIdpSafe(): ReturnType<typeof useIdp> | null {
  try {
    return useIdp();
  } catch {
    return null;
  }
}

export default function LoginFlowDiagnosticsPanel(): React.ReactElement {
  const { message } = App.useApp();
  const { Text } = Typography;
  const store = useGlobalStore.getState();
  const idp = useIdpSafe();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState<boolean>(true);
  const [view, setView] = useState<'raw' | 'tree'>('raw');
  const [json, setJson] = useState<unknown | null>(null);
  const [busy, setBusy] = useState(false);

  // Effective IDP base URL (for links/display); the client reads its own base from provider
  const base = useMemo(() => normalizeBase(getEnv('VITE_IDP_BASE_URL') || ''), []);
  // Resolve audience similar to IdpProvider: meta -> env -> default
  const audience = (() => {
    try {
      const meta = document.querySelector('meta[name="idp-audience"]') as HTMLMetaElement | null;
      const fromMeta = meta?.content?.trim();
      if (fromMeta) return fromMeta;
    } catch {
      // ignore
    }
    const env = getEnv('VITE_IDP_AUDIENCE');
    return env || 'control-admin';
  })();

  // Track CSRF cookie presence for visibility
  const readXsrf = () => {
    try {
      const cookie = typeof document !== 'undefined' ? document.cookie : '';
      const hit = cookie
        .split(';')
        .map((s) => s.trim())
        .find((p) => p.startsWith('XSRF-TOKEN='));
      if (!hit) return undefined;
      return decodeURIComponent(hit.split('=')[1] || '');
    } catch {
      return undefined;
    }
  };
  const [xsrf, setXsrf] = useState<string | undefined>(() => readXsrf());

  const copyOut = async () => {
    try {
      const out = document.getElementById('login-flow-output') as HTMLTextAreaElement | null;
      if (!out?.value) return;
      await navigator.clipboard.writeText(out.value);
      message?.success('Copied JSON to clipboard');
    } catch {
      message?.warning('Copy failed. Select and copy manually.');
    }
  };

  // Helpers for OAuth hash parsing
  const parseHash = () => {
    try {
      const h = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
      const p = new URLSearchParams(h);
      return {
        accessToken: p.get('accessToken') || undefined,
        refreshToken: p.get('refreshToken') || undefined,
        jti: p.get('jti') || undefined,
      } as { accessToken?: string; refreshToken?: string; jti?: string };
    } catch {
      return {} as { accessToken?: string; refreshToken?: string; jti?: string };
    }
  };
  const [hashTokens, setHashTokens] = useState(parseHash());

  return (
    <Card title="App Login Flow (Diagnostics)" style={{ marginTop: 16 }}>
      <Text type="secondary">
        Uses the app's configured IDP client (axios + interceptors) to exercise the standard login
        and refresh logic, then applies tokens into the app store when requested.
      </Text>
      <Divider />

      {/* OAuth helpers */}
      <Space orientation="vertical" style={{ width: '100%' }} size="small">
        <Text strong>OAuth start links</Text>
        {(() => {
          const origin =
            typeof window !== 'undefined' && window.location?.origin
              ? window.location.origin
              : 'http://localhost:4200';
          const googleUrl = `${base}/api/auth/google?returnTo=${encodeURIComponent(origin)}`;
          const facebookUrl = `${base}/api/auth/facebook?returnTo=${encodeURIComponent(origin)}`;
          return (
            <Space wrap>
              <Button
                type="default"
                size="small"
                href={googleUrl || undefined}
                target="_blank"
              >
                Start Google OAuth
              </Button>
              <Button
                type="default"
                size="small"
                href={facebookUrl || undefined}
                target="_blank"
              >
                Start Facebook OAuth
              </Button>
              <Tag color={hashTokens.accessToken ? 'green' : 'default'}>
                hash.accessToken: {hashTokens.accessToken ? 'present' : 'missing'}
              </Tag>
              <Tag color={hashTokens.refreshToken ? 'green' : 'red'}>
                hash.refreshToken: {hashTokens.refreshToken ? 'present' : 'missing'}
              </Tag>
              <Tag color={hashTokens.jti ? 'green' : 'default'}>
                hash.jti: {hashTokens.jti ? 'present' : 'missing'}
              </Tag>
              <Button size="small" onClick={() => setHashTokens(parseHash())}>
                Reparse hash
              </Button>
              <Button
                size="small"
                onClick={() => {
                  if (typeof window !== 'undefined')
                    window.history.replaceState(
                      null,
                      document.title,
                      window.location.pathname + window.location.search,
                    );
                  setHashTokens({
                    accessToken: undefined,
                    refreshToken: undefined,
                    jti: undefined,
                  });
                }}
              >
                Clear URL hash
              </Button>
            </Space>
          );
        })()}
      </Space>
      <Divider />

      <Space wrap size="middle">
        <Tag color="purple">IDP: {displayBase(getEnv('VITE_IDP_BASE_URL'))}</Tag>
        <Tag color="geekblue">API: {displayBase(getEnv('VITE_ACCESS_CONTROL_BASE_URL'))}</Tag>
        <Tag>Audience: {audience}</Tag>
        <Tag>Store access: {store.token ? redact(store.token) : '—'}</Tag>
        <Tag color={xsrf ? 'green' : 'default'}>CSRF cookie: {xsrf ? 'present' : 'missing'}</Tag>
        <Button
          size="small"
          onClick={async () => {
            try {
              if (idp) {
                await idp.axios.get('/api/auth/csrf');
              } else {
                await fetch(`${base}/api/auth/csrf`, { credentials: 'include' });
              }
              setTimeout(() => setXsrf(readXsrf()), 0);
              message?.success('Fetched CSRF token');
            } catch {
              message?.warning('Failed to fetch CSRF token');
            }
          }}
        >
          Ensure CSRF cookie
        </Button>
      </Space>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
        <Input
          style={{ maxWidth: 320 }}
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input.Password
          style={{ maxWidth: 240 }}
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Switch checked={remember} onChange={setRemember} />
        <Text type="secondary">Remember</Text>

        <Button
          type="primary"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              let status = 0;
              let ok = false;
              let body: unknown = null;
              if (idp) {
                const res = await idp.axios.post(
                  '/api/auth/login',
                  { email, password },
                  { headers: { Accept: 'application/json' } },
                );
                status = res.status;
                ok = res.status >= 200 && res.status < 300;
                body = res.data;
              } else {
                const xsrfVal = readXsrf();
                const res = await fetch(`${base}/api/auth/login`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Client-Audience': audience,
                    ...(xsrfVal ? { 'X-XSRF-Token': xsrfVal } : {}),
                  },
                  body: JSON.stringify({ email, password }),
                });
                status = res.status;
                ok = res.ok;
                const text = await res.text();
                try {
                  body = text ? JSON.parse(text) : null;
                } catch {
                  body = { raw: text };
                }
              }
              const pretty = JSON.stringify(
                {
                  status,
                  ok,
                  endpoint: `${idp?.axios.defaults.baseURL || base || ''}/api/auth/login`,
                  body,
                },
                null,
                2,
              );
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              if (out) out.value = pretty;
              setJson({
                status,
                ok,
                endpoint: `${idp?.axios.defaults.baseURL || base || ''}/api/auth/login`,
                body,
              });
              const accessToken = (body as Record<string, unknown>)?.['accessToken'];
              const refreshToken = (body as Record<string, unknown>)?.['refreshToken'];
              const jti = (body as Record<string, unknown>)?.['jti'];
              if (typeof accessToken === 'string') {
                useGlobalStore
                  .getState()
                  .login(accessToken, String(refreshToken || ''), String(jti || ''), remember);
                message?.success('Login successful • tokens applied');
              } else if ((body as Record<string, unknown>)?.['mfaRequired']) {
                message?.info('MFA required — complete MFA flow to proceed');
              } else {
                message?.info('Login response did not contain accessToken');
              }
              setXsrf(readXsrf());
            } catch (e) {
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              const text = String((e as Error)?.message || e);
              if (out) out.value = text;
              setJson({ error: text });
              message?.error('Login failed');
            } finally {
              setBusy(false);
            }
          }}
        >
          Run login (app client)
        </Button>
        <Button
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const refreshToken = useGlobalStore.getState().refreshToken;
              const jti = useGlobalStore.getState().jti;
              let status = 0;
              let ok = false;
              let body: unknown = null;
              if (idp) {
                if (refreshToken && jti) {
                  const res = await idp.authApi.authControllerRefresh('', {
                    refreshToken,
                    jti,
                  } as unknown as { refreshToken: string; jti: string });
                  status = res.status as unknown as number;
                  ok = status >= 200 && status < 300;
                  body = res.data as unknown;
                } else {
                  const res = await idp.axios.post(
                    '/api/auth/refresh',
                    {},
                    { headers: { Accept: 'application/json' } },
                  );
                  status = res.status;
                  ok = res.status >= 200 && res.status < 300;
                  body = res.data as unknown;
                }
              } else {
                const xsrfVal = readXsrf();
                const res = await fetch(`${base}/api/auth/refresh`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Client-Audience': audience,
                    ...(xsrfVal ? { 'X-XSRF-Token': xsrfVal } : {}),
                  },
                  body:
                    refreshToken && jti
                      ? JSON.stringify({ refreshToken, jti })
                      : JSON.stringify({}),
                });
                status = res.status;
                ok = res.ok;
                const text = await res.text();
                try {
                  body = text ? JSON.parse(text) : null;
                } catch {
                  body = { raw: text };
                }
              }
              const pretty = JSON.stringify(
                {
                  status,
                  ok,
                  endpoint: `${idp?.axios.defaults.baseURL || base || ''}/api/auth/refresh`,
                  body,
                },
                null,
                2,
              );
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              if (out) out.value = pretty;
              setJson({
                status,
                ok,
                endpoint: `${idp?.axios.defaults.baseURL || base || ''}/api/auth/refresh`,
                body,
              });
              const accessToken2 = (body as Record<string, unknown>)?.['accessToken'];
              const refreshToken2 = (body as Record<string, unknown>)?.['refreshToken'];
              const jti2 = (body as Record<string, unknown>)?.['jti'];
              if (typeof accessToken2 === 'string') {
                useGlobalStore
                  .getState()
                  .login(accessToken2, String(refreshToken2 || ''), String(jti2 || ''), true);
                message?.success('Refresh successful • tokens rotated/applied');
              } else {
                message?.info('Refresh response did not contain accessToken');
              }
              setXsrf(readXsrf());
            } catch (e) {
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              const text = String((e as Error)?.message || e);
              if (out) out.value = text;
              setJson({ error: text });
              message?.error('Refresh failed');
            } finally {
              setBusy(false);
            }
          }}
        >
          Test refresh
        </Button>
        <Button
          danger
          onClick={() => {
            useGlobalStore.getState().logout();
            message?.success('Cleared tokens');
          }}
        >
          Clear tokens
        </Button>
        <Button onClick={() => void copyOut()}>Copy JSON</Button>
        <Button
          onClick={() => {
            try {
              const raw = useGlobalStore.getState().token;
              if (!raw) {
                message?.warning('No access token in store');
                return;
              }
              const parts = raw.split('.');
              if (parts.length < 2) throw new Error('Invalid JWT');
              const b64urlToUtf8 = (s: string) => {
                const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
                const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
                const decoded =
                  typeof atob === 'function'
                    ? atob(b64)
                    : Buffer.from(b64, 'base64').toString('binary');
                const bytes = Uint8Array.from(decoded, (c) => c.charCodeAt(0));
                return new TextDecoder().decode(bytes);
              };
              const header = JSON.parse(b64urlToUtf8(parts[0]));
              const payload = JSON.parse(b64urlToUtf8(parts[1]));
              const pretty = JSON.stringify({ header, payload }, null, 2);
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              if (out) out.value = pretty;
              setJson({ header, payload });
              message?.success('Decoded access token');
            } catch (e) {
              const text = String((e as Error)?.message || e);
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              if (out) out.value = text;
              setJson({ error: text });
              message?.error('Failed to decode token');
            }
          }}
        >
          Decode access token
        </Button>

        <Button
          onClick={async () => {
            const apiBase = normalizeBase(getEnv('VITE_ACCESS_CONTROL_BASE_URL') || '');
            try {
              const token = useGlobalStore.getState().token;
              const res = await fetch(`${apiBase}/api/roles?take=1`, {
                headers: {
                  Accept: 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
              });
              const bodyText = await res.text();
              let parsed: unknown = null;
              try {
                parsed = bodyText ? JSON.parse(bodyText) : null;
              } catch {
                parsed = { raw: bodyText };
              }
              const pretty = JSON.stringify(
                {
                  status: res.status,
                  ok: res.ok,
                  endpoint: `${apiBase}/api/roles?take=1`,
                  body: parsed,
                },
                null,
                2,
              );
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              if (out) out.value = pretty;
              setJson({
                status: res.status,
                ok: res.ok,
                endpoint: `${apiBase}/api/roles?take=1`,
                body: parsed,
              });
              message?.info(res.ok ? 'Protected call OK' : `Protected call HTTP ${res.status}`);
            } catch (e) {
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              const text = String((e as Error)?.message || e);
              if (out) out.value = text;
              setJson({ error: text });
              message?.error('Protected call failed');
            }
          }}
        >
          Test protected call (/roles?take=1)
        </Button>

        <Button
          onClick={async () => {
            const apiBase = normalizeBase(getEnv('VITE_ACCESS_CONTROL_BASE_URL') || '');
            try {
              const res = await fetch(`${apiBase}/api/roles?take=1`, {
                headers: { Accept: 'application/json', Authorization: 'Bearer invalid.token' },
              });
              const bodyText = await res.text();
              let parsed: unknown = null;
              try {
                parsed = bodyText ? JSON.parse(bodyText) : null;
              } catch {
                parsed = { raw: bodyText };
              }
              const pretty = JSON.stringify(
                {
                  status: res.status,
                  ok: res.ok,
                  endpoint: `${apiBase}/api/roles?take=1`,
                  note: 'Simulated 401 with invalid token (no refresh here)',
                  body: parsed,
                },
                null,
                2,
              );
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              if (out) out.value = pretty;
              setJson({
                status: res.status,
                ok: res.ok,
                endpoint: `${apiBase}/api/roles?take=1`,
                body: parsed,
              });
              message?.info(`Simulated call returned HTTP ${res.status}`);
            } catch (e) {
              const out = document.getElementById(
                'login-flow-output',
              ) as HTMLTextAreaElement | null;
              const text = String((e as Error)?.message || e);
              if (out) out.value = text;
              setJson({ error: text });
              message?.error('Simulated 401 call failed');
            }
          }}
        >
          Simulate 401 (no refresh)
        </Button>
      </div>

      <div style={{ marginTop: 12 }}>
        <Collapse
          size="small"
          items={[
            {
              key: 'raw-json',
              label: 'Raw JSON (click to expand)',
              children: (
                <div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                    <Segmented
                      options={[
                        { label: 'Raw', value: 'raw' },
                        { label: 'Tree', value: 'tree' },
                      ]}
                      value={view}
                      onChange={(v) => setView(v as 'raw' | 'tree')}
                    />
                  </div>
                  {view === 'raw' ? (
                    <textarea
                      id="login-flow-output"
                      style={{ width: '100%', minHeight: 240 }}
                      readOnly
                    />
                  ) : json ? (
                    <div
                      style={{
                        maxHeight: 360,
                        overflow: 'auto',
                        border: '1px solid #f0f0f0',
                        borderRadius: 6,
                        padding: 8,
                      }}
                    >
                      <pre style={{ margin: 0 }}>{JSON.stringify(json, null, 2)}</pre>
                    </div>
                  ) : (
                    <Alert type="info" title="Run login or refresh to populate JSON view" />
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </Card>
  );
}
